import { ExternalShareType } from "@amzn/ads-discovery-theme-service-typescript-client";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Modal, Switch, Text } from "@amzn/storm-ui";
import { Form, FormRow } from "@amzn/storm-ui-column-form-kit";
import { useMemo, useEffect } from "react";
import { useState } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { SHARE_PITCHED_LINK_HELP_TIP } from "../../../../config/constants";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import { useSharePitchContentAction } from "../../../../hooks/pitchedContent/useSharePitchContentAction";
import {
    defaultExternalShareInfo,
    shareContentStore,
} from "../../../../store/shareContent";
import { StatusTextState } from "../../../../types/common";
import type { MarketingNarrativeFileFormats } from "../../../../types/marketingNarrativeFileFormats";
import { isLoading } from "../../../../utils/api.utils";
import { copyToClipboard } from "../../../../utils/clipboard.utils";
import { DateFormat, formatDate } from "../../../../utils/date.utils";
import { isShareableAsPdf } from "../../../../utils/insightPreview.utils";
import { getPhoenixContentInfoFromStore } from "../../../../utils/phoenix.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";
import { StatusTextWithLoadState } from "../../../common/StatusTextWithLoadState";
import {
    OptionsContainer,
    ModalContainer,
    InputComponentTextButton,
    DivFlexSpaceBetween,
} from "../../../common/styled.components";

import { SharePitchedContentFooter } from "./SharePitchedContentFooter";

const DivFlexColumnGap = styled.div`
    display: flex;
    flex-direction: column;
    align-content: flex-start;
    gap: 5px;
`;

export const SharePitchedContentModal: React.FunctionComponent = () => {
    const [isPdfFileType, setIsPdfFileType] = useState<boolean>(false);
    const [canShareAsPdf, setCanShareAsPdf] = useState<boolean>(false);

    const [
        isModalOpen,
        identifier,
        version,
        externalShareInfo,
        externalShareType,
        setExternalShareInfo,
        setExternalShareType,
        setVersion,
        isShareActive,
        setIsShareActive,
        originalFileFormat,
        reset,
    ] = useStore(shareContentStore, (state) => [
        state.isModalOpen,
        state.identifier,
        state.version,
        state.externalShareInfo,
        state.externalShareType,
        state.setExternalShareInfo,
        state.setExternalShareType,
        state.setVersion,
        state.isShareActive,
        state.setIsShareActive,
        state.originalFileFormat,
        state.reset,
    ]);

    useEffect(() => {
        setCanShareAsPdf(
            isShareableAsPdf(
                originalFileFormat as MarketingNarrativeFileFormats,
            ),
        );
    }, [originalFileFormat]);

    useEffect(() => {
        setIsShareActive(
            externalShareInfo !== undefined &&
                externalShareInfo.externalShareUrl !==
                    defaultExternalShareInfo.externalShareUrl,
        );
    }, [externalShareInfo]);

    useEffect(() => {
        const externalShareType = isPdfFileType
            ? ExternalShareType.PDF
            : ExternalShareType.ORIGINAL;
        setExternalShareType(externalShareType);
    }, [isPdfFileType]);

    const [sharePitchUrlState, sharePitchUrlAction] =
        useSharePitchContentAction({
            contentId: identifier,
            version,
            externalShareType,
        });

    const generateShareUrl = async () => {
        const content = await sharePitchUrlAction.execute();
        logWithTakt(TELEMETRY_ID.PITCHED_CONTENT_GENERATE_SHARE_LINK, {
            contentId: identifier,
            externalShareType,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.PITCHED_CONTENT_GENERATE_SHARE_LINK,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    InsightType.PITCHED_CONTENT,
                    identifier,
                ),
                customData: {
                    externalShareType,
                },
            },
        );
        if (content && content.externalShareUrl) {
            setExternalShareInfo({
                ...defaultExternalShareInfo,
                ...content,
            });
            setVersion(content.latestVersion!);
        }
    };

    const handleCopyUrl = () => {
        copyToClipboard(externalShareInfo.externalShareUrl);
        logWithTakt(TELEMETRY_ID.PITCHED_CONTENT_SHARE_URL, {
            contentId: identifier,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.PITCHED_CONTENT_SHARE_URL,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    InsightType.PITCHED_CONTENT,
                    identifier,
                ),
            },
        );
    };

    const handleCopyPasscode = () => {
        copyToClipboard(externalShareInfo.externalSharePasscode);
        logWithTakt(TELEMETRY_ID.PITCHED_CONTENT_SHARE_PASSCODE, {
            contentId: identifier,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.PITCHED_CONTENT_SHARE_PASSCODE,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    InsightType.PITCHED_CONTENT,
                    identifier,
                ),
            },
        );
    };

    const sharePitchStatus = useMemo(() => {
        return isLoading(sharePitchUrlState.status)
            ? StatusTextState.LOADING
            : isShareActive
              ? StatusTextState.ACTIVE
              : StatusTextState.INACTIVE;
    }, [isShareActive, sharePitchUrlState.status]);

    const onClose = () => {
        sharePitchUrlAction.reset();
        setIsPdfFileType(false);
        reset();
    };

    return (
        <Modal
            id="share-pitched-content-modal"
            data-testid="share-pitched-content-modal"
            isOpen={isModalOpen}
            header="Share decks"
            onClose={onClose}
            padding="xsmall"
            style={{ zIndex: "800" }}
            footer={
                <SharePitchedContentFooter
                    handleClose={onClose}
                    generateShareUrl={generateShareUrl}
                />
            }
        >
            <OptionsContainer>
                <ModalContainer style={{ width: "40vw" }}>
                    <Form id="share-pitched-content-form">
                        <FormRow
                            helpTipText={SHARE_PITCHED_LINK_HELP_TIP}
                            label="Link"
                            labelTopSpacing="0"
                            id="share-pitched-url-form"
                            controlComponents={[
                                () => (
                                    <DivFlexSpaceBetween>
                                        <Text
                                            id="share-pitched-url"
                                            data-testid="share-pitched-url"
                                        >
                                            {externalShareInfo.externalShareUrl}
                                        </Text>
                                        {isShareActive && (
                                            <InputComponentTextButton
                                                data-testid="share-pitched-content-copy-url"
                                                onClick={() =>
                                                    void handleCopyUrl()
                                                }
                                            >
                                                Copy link
                                            </InputComponentTextButton>
                                        )}
                                    </DivFlexSpaceBetween>
                                ),
                            ]}
                        />
                        <FormRow
                            label="Access Code"
                            labelTopSpacing="0"
                            id="share-pitched-passcode-form"
                            controlComponents={[
                                () => (
                                    <DivFlexSpaceBetween>
                                        <Text
                                            id="share-pitched-passcode"
                                            data-testid="share-pitched-passcode"
                                        >
                                            {
                                                externalShareInfo.externalSharePasscode
                                            }
                                        </Text>
                                        {isShareActive && (
                                            <InputComponentTextButton
                                                data-testid="share-pitched-content-copy-passcode"
                                                onClick={() =>
                                                    void handleCopyPasscode()
                                                }
                                            >
                                                Copy code
                                            </InputComponentTextButton>
                                        )}
                                    </DivFlexSpaceBetween>
                                ),
                            ]}
                        />
                        {canShareAsPdf && (
                            <FormRow
                                label="Link as PDF"
                                labelTopSpacing="0"
                                id="generate-pdf-switch-form"
                                controlComponents={[
                                    () => (
                                        <>
                                            <Switch
                                                id="generate-pdf-switch"
                                                data-testid="generate-pdf-switch"
                                                onChange={(isPdfFileType) =>
                                                    void setIsPdfFileType(
                                                        isPdfFileType,
                                                    )
                                                }
                                                toggled={isPdfFileType}
                                            />
                                        </>
                                    ),
                                ]}
                            />
                        )}
                        <FormRow
                            label="Status"
                            labelTopSpacing="0"
                            id="share-pitched-status-form"
                            controlComponents={[
                                () => (
                                    <DivFlexColumnGap>
                                        <StatusTextWithLoadState
                                            id="share-pitched-content-status"
                                            status={sharePitchStatus}
                                            activeText="Active"
                                            inactiveText="Inactive"
                                            loadingText={
                                                "Link generation in progress"
                                            }
                                        />
                                        {isShareActive && (
                                            <Text color="secondary">{`Expires on ${formatDate(externalShareInfo.externalShareExpiryDate, DateFormat.LONG_DATE_FORMAT)}`}</Text>
                                        )}
                                    </DivFlexColumnGap>
                                ),
                            ]}
                        />
                    </Form>
                </ModalContainer>
            </OptionsContainer>
        </Modal>
    );
};
